import React from 'react';
import { v4 } from 'uuid';
import { toast } from '@cotera/client/app/components/ui';
import { BroadcastEventType } from '@cotera/api';
import type {
  BroadcastEventPayloadMap,
  ResourceUpdatedPayload,
} from '@cotera/api';
import {
  BaseModel,
  EventWatchable,
  Watchable,
  useInScopeModel,
  type ReadonlyEventWatchable,
  type ReadonlyWatchable,
} from '@cotera/client/v0/actions/framework';
import type { DuckDbWriteTarget } from '@cotera/client/app/etc/duckdb';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import { isDatasetTriggerCriterion } from '@cotera/sdk/core';
import type { DuckDbLayerGrid } from '@cotera/client/app/etc/duckdb/layers';
import type { DatasetPreviewLayer, DatasetRowIdCursor } from './preview';
import {
  RowDetail,
  automationRowDetailHeight,
  automationsAffectedBy,
  buildAutomationProjection,
  clearAutomationRunsPending,
  ensureAutomationsTable,
  loadAutomationOverlay,
  loadAutomationStateBreakdown,
  markAutomationRunsPending,
  markAutomationRunsPendingForRows,
  repaintLoadedRows,
  selectOverlayValuesForRows,
  summaryColumn,
  upsertAutomationRun,
  workflowOutputColumn,
  type DatasetAutomationWorkflow,
} from './dataset-automations';

/**
 * How often the snapshot re-syncs itself even when no event arrived.
 *
 * Long, because per-run changes already stream in live — this only has to
 * catch a wiring change or an event the socket dropped, neither of which is
 * urgent enough to pay for a fast poll.
 */
const AUTOMATION_RECONCILE_MS = 60_000;
/** Faster, but only runs while the socket is down. */
const AUTOMATION_FALLBACK_POLL_MS = 10_000;
/**
 * How long a burst of `resource-updated` events has to go quiet before the
 * snapshot reloads.
 *
 * Trailing edge, so a burst costs one reload at its end rather than one per
 * event — and a reload is two round trips plus a DuckDB load, not one request.
 * 500ms matches the other resource-list watchables.
 */
const AUTOMATION_EVENT_DEBOUNCE_MS = 500;

/**
 * Rows per `trigger-now` request.
 *
 * The endpoint takes an unbounded list, but dispatching is sequential per
 * (row x workflow) on the server, so one request for ten thousand rows is one
 * very long request with no way to report progress or to stop. Batching turns it
 * into a stream of short ones: the run count climbs as they land, and a stop
 * takes effect at the next boundary rather than never.
 */
const RUN_BATCH_SIZE = 25;

/**
 * How long a burst of run-finished events has to go quiet before the batch's
 * counts are re-read.
 *
 * Short enough that the bar tracks the work, long enough that a fan-out of
 * thousands costs a handful of reads rather than thousands.
 */
const RUN_PROGRESS_REFRESH_MS = 750;

/**
 * A bulk run in flight on this dataset, or `null` when there is none.
 *
 * No longer local to the tab. The server keeps a record of each batch, so this
 * is adopted on load as readily as it is set by starting one — a reload picks up
 * its own run where it left off, and a colleague's run shows up here too. The
 * counts below come from that record rather than from what this tab happened to
 * witness, which is what makes them right in a tab that was not watching.
 */
export type DatasetRunProgress = {
  /** Minted here or read back from the server; names the batch for every call. */
  groupId: string;
  /**
   * How the runs are being sent.
   *
   * `dispatch` is this tab looping batches through `trigger-now` (the filtered
   * and needs-run scopes), where `dispatched`/`total` are real counts and Stop
   * interrupts the loop. `replay` is the server re-evaluating the whole dataset
   * (the "all rows" scope): there is no local loop and no count to report — the
   * evaluator owns the dispatch — so the UI shows an indeterminate "running in
   * the background" and Stop cancels it server-side by its group id.
   */
  kind: 'dispatch' | 'replay';
  /** Runs the server has confirmed it queued. Stays 0 for a replay. */
  dispatched: number;
  /** Runs this tab set out to queue. 0 for a replay: the server decides. */
  total: number;
  /**
   * Whether this tab is still sending batches. Never true for a replay — the
   * server owns that dispatch from the moment the request returns.
   */
  dispatching: boolean;
  /** Runs in the batch, as the server counts them. 0 until the first lands. */
  runs: number;
  /** Of those, the ones that have finished — however they finished. */
  finished: number;
  /**
   * `finished / runs` as a whole percent, from the server so every reader
   * agrees what it means.
   */
  percentComplete: number;
  /**
   * Whether the denominator can still grow.
   *
   * True while the batch is still being dispatched, which is why the bar is
   * drawn as indeterminate then: the percentage can go *down* as more runs
   * join, and a bar that ran backwards would read as a bug.
   */
  estimated: boolean;
  /** Dispatch is closed and nothing is pending: the batch is over. */
  settled: boolean;
  /** True when this tab did not start the batch — a reload, or a colleague. */
  adopted: boolean;
};

/** A batch with none of its runs seen yet; the shape a dispatch starts in. */
const NO_COUNTS = {
  runs: 0,
  finished: 0,
  percentComplete: 0,
  estimated: true,
  settled: false,
  adopted: false,
} as const;

type AutomationSnapshot = {
  workflows: DatasetAutomationWorkflow[];
  truncated: boolean;
  /** False until a load has actually landed, so columns are not built early. */
  ready: boolean;
  /**
   * False only before the first wiring fetch has settled. `ready` cannot stand
   * in for this: it stays false for a dataset with no automations, so gating the
   * empty cell on it alone would shimmer such a dataset forever. This flips true
   * the moment a load returns — with workflows or not — so the empty cell knows
   * the difference between "still finding out" and "found nothing".
   */
  settled: boolean;
};

const EMPTY_SNAPSHOT: AutomationSnapshot = {
  workflows: [],
  truncated: false,
  ready: false,
  settled: false,
};

/** The settled-but-empty result: the wiring fetch landed, nothing is bound. */
const SETTLED_EMPTY_SNAPSHOT: AutomationSnapshot = {
  workflows: [],
  truncated: false,
  ready: false,
  settled: true,
};

/**
 * What the model is currently looking at.
 *
 * Held as a watchable rather than taken in the constructor because all three
 * arrive from a fetch, not the route: the page mounts, renders, and only then
 * learns which dataset and artifact it is showing.
 */
type DatasetAutomationsSource = {
  datasetId: string | null;
  /** The artifact this browser loaded; bounds the overlay to its rows. */
  artifactId: string | null;
  rowIdColumn: string | null;
};

const EMPTY_SOURCE: DatasetAutomationsSource = {
  datasetId: null,
  artifactId: null,
  rowIdColumn: null,
};

const sameSource = (
  left: DatasetAutomationsSource,
  right: DatasetAutomationsSource
): boolean =>
  left.datasetId === right.datasetId &&
  left.artifactId === right.artifactId &&
  left.rowIdColumn === right.rowIdColumn;

/**
 * The endpoints this model calls, named structurally rather than as
 * `Pick<TenantedClient, 'resource'>`.
 *
 * A tenanted client is the whole API surface, and nothing short of one could be
 * passed here — which put every one of this model's behaviours out of reach of
 * a test that does not fabricate a client through a cast. This says what it
 * actually uses instead; a real client still satisfies it.
 */
type DatasetAutomationsClient = {
  resource: {
    workflow: Pick<
      TenantedClient['resource']['workflow'],
      | 'datasetAutomations'
      | 'datasetAutomationOverlay'
      | 'triggerNow'
      | 'replayDataset'
      | 'cancelDatasetRunGroup'
      | 'collectDatasetRunGroupRows'
      | 'startDatasetRunGroup'
      | 'datasetRunGroups'
    >;
  };
};

type DatasetAutomationsModelProps = {
  /**
   * Scope identity only — the dataset the *page* is for, known from the route.
   * What the model actually works on arrives later, through {@link
   * DatasetAutomationsModel.setSource}, because the artifact and its row-id
   * column come from a fetch. Two dataset views must not share one model, which
   * is what registering under this id prevents.
   */
  datasetId: string;
  client: DatasetAutomationsClient;
  /**
   * Narrowed to what this model actually does with the database — run SQL and
   * register a file — rather than the whole handle, so the shared preview
   * database is not the only thing that can be passed in.
   */
  duckDb: Promise<DuckDbWriteTarget>;
};

/**
 * Which automations watch a dataset, with their per-row state loaded into
 * DuckDB and an overlay ready to hand to the preview grid.
 *
 * {@link overlayWatchable} holds `undefined` whenever there is nothing to show
 * — no automations, no row-id column to join on, or an overlay too large to be
 * honest about — so the grid renders exactly as it did before.
 *
 * Derived rather than assembled per render, because the overlay is a bundle of
 * closures that no equality check can dedupe: replacing it rebuilds the grid's
 * view model and resets sorts, selection and expansion. Its stability comes
 * entirely from its sources, and every source here compares by content.
 */
export class DatasetAutomationsModel extends BaseModel {
  /** Undefined when the grid should render with no automation columns. */
  readonly layerWatchable: ReadonlyWatchable<DatasetPreviewLayer | undefined>;
  readonly workflowsWatchable: ReadonlyWatchable<DatasetAutomationWorkflow[]>;
  /**
   * The name of the org that owns the dataset, when it differs from the
   * reader's own — i.e. the dataset was shared in. Null on an unshared
   * dataset, or before the first wiring fetch has landed.
   */
  readonly ownerOrgNameWatchable: ReadonlyWatchable<string | null>;
  readonly truncatedWatchable: ReadonlyWatchable<boolean>;
  /**
   * False until the overlay fetch has landed at least once. The wiring — and so
   * the column — is published optimistically before that fetch (see the `load`
   * closure), so a bound workflow's per-row state reads as `never` until the
   * overlay arrives. The summary cell watches this to shimmer in place of that
   * premature "never run" until the real statuses are in.
   */
  readonly readyWatchable: ReadonlyWatchable<boolean>;
  /**
   * False only until the first wiring fetch settles. The summary cell watches
   * this to shimmer its empty state while the automations list is still being
   * fetched, instead of flashing the "no automations" pill at a dataset that
   * may well have some.
   */
  readonly settledWatchable: ReadonlyWatchable<boolean>;
  /**
   * The bulk run this tab started, until it is stopped or another replaces it.
   *
   * On the model rather than in the control that starts it, so opening and
   * closing the run menu does not lose a batch that is still dispatching — and,
   * more to the point, so it stays out of the overlay, whose identity rebuilds
   * the whole grid view model.
   */
  readonly runProgressWatchable: ReadonlyWatchable<DatasetRunProgress | null>;

  private readonly _client: DatasetAutomationsClient;
  private readonly _duckDb: Promise<DuckDbWriteTarget>;
  private readonly _source: Watchable<DatasetAutomationsSource>;
  private readonly _wiring: Watchable<DatasetAutomationWorkflow[]>;
  private readonly _ownerOrgName: Watchable<string | null>;
  /**
   * Which of the bound workflows the grid expands into an output column, saved
   * on the dataset. Held separately from the wiring because it is a view setting
   * the user changes, not something a reload of the automations wiring produces
   * — and it is part of the layer's identity, so it compares by content.
   */
  private readonly _expandedWorkflowIds: Watchable<readonly string[]>;
  private readonly _snapshot: ReadonlyEventWatchable<AutomationSnapshot>;
  private readonly _live: ReadonlyEventWatchable<number>;
  /**
   * The grid registers itself through the overlay's `subscribe`; the watchables
   * below patch through it. Held rather than passed so that re-registering does
   * not tear them down, and so a load that lands before the grid mounts simply
   * finds null.
   */
  private _grid: DuckDbLayerGrid | null = null;
  /**
   * The last layer handed out, with the inputs it was built from.
   *
   * {@link layerWatchable} is derived, and a derived watchable re-evaluates
   * whenever *any* source it read notifies — including a notification that
   * carries an identical value. Its result is a bundle of closures, so every
   * re-evaluation would otherwise mint an object no equality check downstream
   * can match, and layer identity is what the grid's entire view model is
   * memoized on: columns, sorts, filters, selection, expansion and scroll are
   * all rebuilt, which reads as a flash over live data. Caching on the inputs
   * makes the layer change only when it actually describes something new.
   */
  private _layer: { signature: string; layer: DatasetPreviewLayer } | null =
    null;
  private _tick = 0;
  private readonly _runProgress: Watchable<DatasetRunProgress | null>;
  /** Flipped by `stopRun`; read at each batch boundary. */
  private _stopRequested = false;

  constructor(props: DatasetAutomationsModelProps) {
    super(props.datasetId);
    this._client = props.client;
    this._duckDb = props.duckDb;
    this._source = Watchable.fromValue<DatasetAutomationsSource>(EMPTY_SOURCE, {
      equalityFn: sameSource,
    });

    /**
     * The workflow list alone, separate from the run data.
     *
     * Everything the grid needs to *show* the column comes from here; the data
     * only fills it in. Keeping them apart is what lets the column appear on
     * the first paint rather than after two round trips and a DuckDB load.
     *
     * The equality function is load-bearing. A `Watchable` notifies on every
     * `set` by default, and this one is republished by each reload — which a
     * run triggers once per dispatched workflow, via `ResourceUpdated`. Since
     * the overlay is derived from this list, a fresh array of identical
     * contents would change the overlay's identity and rebuild the whole grid
     * view model. Compare by content so an unchanged wiring stays silent.
     */
    this._wiring = Watchable.fromValue<DatasetAutomationWorkflow[]>([], {
      equalityFn: (left, right) =>
        JSON.stringify(left) === JSON.stringify(right),
    });
    this.workflowsWatchable = this._wiring;

    this._ownerOrgName = Watchable.fromValue<string | null>(null);
    this.ownerOrgNameWatchable = this._ownerOrgName;

    // Same content-equality reason as the wiring: the page republishes this on
    // every resource refresh, and a fresh array of identical ids would rebuild
    // the layer — and with it the whole grid view model — for nothing.
    this._expandedWorkflowIds = Watchable.fromValue<readonly string[]>([], {
      equalityFn: (left, right) =>
        JSON.stringify(left) === JSON.stringify(right),
    });

    this._runProgress = Watchable.fromValue<DatasetRunProgress | null>(null);
    this.runProgressWatchable = this._runProgress;

    /**
     * The wiring, and — when asked for it — the overlay and DuckDB behind it.
     *
     * `includeOverlay` is what separates the two reasons this runs. A resource
     * event means the *bindings* may have moved, and reloading the overlay for
     * it is both wasteful and wrong: wasteful because per-run state already
     * streams in live, and wrong because the overlay is generated from a
     * server-side snapshot that can predate the run whose event triggered this.
     * Replacing the local rows with it then walked a cell that was already
     * showing `running` back to `never run` until the next event pushed it
     * forward again.
     *
     * The overlay is loaded where it cannot regress anything: on mount, when the
     * source changes, on the reconcile cadence, and on the fallback poll — the
     * moments where the local copy is suspect rather than ahead.
     */
    /**
     * The last snapshot this produced, held here rather than read back off
     * `_snapshot`. `runOnMount` can reach `fn` before the assignment below has
     * happened, so the field is not safe to read from inside it.
     */
    let lastSnapshot: AutomationSnapshot = EMPTY_SNAPSHOT;

    const load = async (input: {
      includeOverlay: boolean;
    }): Promise<AutomationSnapshot> => {
      const { datasetId, artifactId } = this._source.snapshot();
      if (datasetId === null) {
        this._wiring.set([]);
        this._ownerOrgName.set(null);
        return EMPTY_SNAPSHOT;
      }
      const wiringRes = await this._client.resource.workflow.datasetAutomations(
        { datasetId }
      );
      if (wiringRes.isErr()) {
        // The fetch is done, it just failed — degrade to the no-automations
        // pill rather than shimmering forever. The reconcile/fallback still
        // retries.
        return SETTLED_EMPTY_SNAPSHOT;
      }
      const workflows = wiringRes.value.triggers.flatMap((trigger) =>
        trigger.workflows.map((workflow) => ({
          id: workflow.id,
          name: workflow.name,
          triggerId: trigger.id,
          writes: workflow.writes.map((write) => write.columnName),
          runMode: trigger.runMode,
          // What the trigger watches for, narrowed from the wire's bare string
          // so an unrecognised criterion omits the cadence line rather than
          // mislabelling it. Carried down from the trigger for the same reason
          // as runMode/canRun.
          criterion: isDatasetTriggerCriterion(trigger.criterion)
            ? trigger.criterion
            : null,
          // Carried down from the trigger, because firing is a property of the
          // trigger and the grid acts on workflows.
          canRun: trigger.canRun,
        }))
      );
      // Known regardless of whether anything is bound, so it is set ahead of
      // the empty-wiring branch below rather than only on the path that
      // builds columns.
      this._ownerOrgName.set(wiringRes.value.ownerOrgName);
      if (workflows.length === 0) {
        this._wiring.set([]);
        return SETTLED_EMPTY_SNAPSHOT;
      }
      // The table the column's SQL joins, created empty before the wiring that
      // turns that join on is published. Cheap — a `CREATE TABLE IF NOT EXISTS`
      // against a database the grid has already loaded — and it is what makes
      // the optimistic publish below safe rather than a catalog error.
      await ensureAutomationsTable(await this._duckDb);
      // Published before the overlay fetch and the DuckDB load, which are the
      // slow half. The columns and their SQL need only the workflow list, and
      // an empty automations table simply reads as "never run" until the data
      // lands and repaints — so the column can be there from the first paint
      // instead of appearing seconds later and rebuilding the grid.
      this._wiring.set(workflows);
      if (!input.includeOverlay) {
        // Carries the previous overlay facts through untouched, so an unchanged
        // wiring leaves the snapshot equal and nothing downstream republishes.
        return {
          workflows,
          truncated: lastSnapshot.truncated,
          ready: lastSnapshot.ready,
          settled: true,
        };
      }
      const overlayRes =
        await this._client.resource.workflow.datasetAutomationOverlay({
          datasetId,
          // Bounds the payload to the rows this browser actually downloaded.
          artifactId,
        });
      // Withdraws the column again, because the wiring was published
      // optimistically above. Only a failed fetch does this: an *empty* overlay
      // is a dataset whose automations have not run yet, which is a column full
      // of "never run" rather than no column at all.
      if (overlayRes.isErr()) {
        this._wiring.set([]);
        return SETTLED_EMPTY_SNAPSHOT;
      }
      const db = await this._duckDb;
      await loadAutomationOverlay(db, {
        datasetId,
        rows: overlayRes.value.rows,
      });
      // Replacing the table is only half the job. Rows already on screen were
      // rendered from the *previous* contents, so without this a reconcile
      // leaves DuckDB correct and the grid visibly stale until some unrelated
      // action happens to re-run the page query.
      //
      // Patched row by row rather than re-queried, for the same reason the
      // live path patches: a re-query would reorder and reset scroll,
      // selection and expansion.
      await repaintLoadedRows(db, {
        datasetId,
        workflows,
        grid: this._grid,
      });
      return {
        workflows,
        truncated: overlayRes.value.truncated,
        ready: true,
        settled: true,
      };
    };

    /**
     * The watchable swallows rejections, and a reload that fails half way
     * through — after the delete, before the insert — leaves DuckDB empty and
     * every row reading as never run. Silently. Worth a line in the console.
     */
    const loadLogged = async (input: {
      includeOverlay: boolean;
    }): Promise<AutomationSnapshot> => {
      try {
        lastSnapshot = await load(input);
        return lastSnapshot;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[dataset-automations] reload threw', error);
        throw error;
      }
    };

    this._snapshot = EventWatchable.for<
      AutomationSnapshot,
      ResourceUpdatedPayload
    >({
      // Bindings and trigger state change through the resource channel.
      event: BroadcastEventType.ResourceUpdated,
      initialValue: EMPTY_SNAPSHOT,
      runOnMount: true,
      filter: (payload) => automationsAffectedBy(payload),
      // What survives the filter still arrives in bursts — a trigger edit
      // touches the trigger and every workflow bound to it — and each reload is
      // a full resync, so only the last one in a burst is worth paying for.
      debounceMs: AUTOMATION_EVENT_DEBOUNCE_MS,
      // Belt and braces for the bursts the debounce window cannot span: a
      // reload is idempotent, so an event arriving mid-flight only needs one
      // more pass afterwards, not one per event.
      serializeRefreshes: true,
      serializeQueueStrategy: 'discard',
      // Self-heals a missed event without the user doing anything. Slow,
      // because per-run changes already arrive live below — this only has to
      // catch wiring changes and dropped events.
      reconcile: { cadence: AUTOMATION_RECONCILE_MS },
      fallback: {
        // Only runs while the socket is down, so the live channel is not
        // feeding this tab anything the overlay could regress.
        fn: () => loadLogged({ includeOverlay: true }),
        interval: AUTOMATION_FALLBACK_POLL_MS,
      },
      equalityFn: (left, right) =>
        JSON.stringify(left) === JSON.stringify(right),
      // `payload === undefined` is this watchable running on its own account —
      // mount, `refresh()` from a source change, or the reconcile — rather than
      // reacting to an event. Only those reload the overlay.
      fn: async (payload) =>
        loadLogged({ includeOverlay: payload === undefined }),
    });
    this.truncatedWatchable = Watchable.from(
      (get) => get(this._snapshot).truncated
    );
    this.readyWatchable = Watchable.from((get) => get(this._snapshot).ready);
    this.settledWatchable = Watchable.from(
      (get) => get(this._snapshot).settled
    );

    /**
     * Per-run updates: upsert into DuckDB, then repaint just that row.
     *
     * Deliberately separate from the snapshot rather than another trigger for
     * it. A refetch would reload the whole overlay and re-run the page query,
     * which is what reorders rows under the cursor; this touches one row and
     * leaves the query alone. Its value is a tick — the work is the side
     * effect.
     */
    this._live = EventWatchable.for<
      number,
      BroadcastEventPayloadMap[typeof BroadcastEventType.RunWorkflow]
    >({
      event: BroadcastEventType.RunWorkflow,
      initialValue: 0,
      runOnMount: false,
      filter: (payload) =>
        this._source.snapshot().datasetId !== null &&
        payload?.datasetId === this._source.snapshot().datasetId &&
        payload?.datasetRowId !== null &&
        payload?.datasetRowId !== undefined,
      // Bursty while a fan-out runs, and each event is a separate row, so
      // they are serialized rather than debounced — dropping one would lose
      // a row's update entirely.
      serializeRefreshes: true,
      fn: async (payload) => {
        this._tick += 1;
        const rowId = payload?.datasetRowId;
        if (
          payload === undefined ||
          payload.datasetId === undefined ||
          rowId === null ||
          rowId === undefined
        ) {
          return this._tick;
        }
        const db = await this._duckDb;
        // Clears any mark this tab left for the pair as a side effect: the
        // upsert replaces every record for it, and the server's word is what
        // the mark was standing in for.
        await upsertAutomationRun(db, {
          datasetId: payload.datasetId,
          datasetRowId: rowId,
          workflowId: payload.workflowId,
          runId: payload.runId,
          status: payload.status,
          finishedAt: payload.finishedAt,
        });
        await this.repaintRow(db, payload.datasetId, rowId);
        // The same event that moved a row also moves the batch it belongs to.
        // Read back rather than counted here: this tab may have missed the
        // first half of the batch, and the server's tally does not care.
        const tracked = this._runProgress.snapshot();
        if (tracked !== null && payload.groupId === tracked.groupId) {
          void this.scheduleRunProgressRefresh();
        }
        return this._tick;
      },
    });

    this.layerWatchable = Watchable.from<DatasetPreviewLayer | undefined>(
      (get) => {
        const { datasetId, rowIdColumn } = get(this._source);
        const workflows = get(this._wiring);
        const expandedIds = get(this._expandedWorkflowIds);
        const truncated = get(this.truncatedWatchable);
        if (
          datasetId === null ||
          rowIdColumn === null ||
          // A truncated overlay would sort and filter against a prefix of the
          // truth, which is worse than not offering the columns at all.
          truncated
        ) {
          return undefined;
        }
        // The expanded set is a subset of the bound workflows, resolved to
        // {workflow, index} pairs so the output column can read the passenger
        // the projection keyed by index. An id that no longer names a bound
        // workflow — removed since the setting was saved — simply drops out.
        const expanded = new Set(expandedIds);
        const expandedColumns = workflows
          .map((workflow, index) => ({ workflow, index }))
          .filter(({ workflow }) => expanded.has(workflow.id))
          .map(({ workflow, index }) => workflowOutputColumn(workflow, index));
        // Everything below reads from these and nothing else, so they are the
        // whole of the layer's identity.
        const signature = JSON.stringify([
          datasetId,
          rowIdColumn,
          workflows,
          expandedIds,
        ]);
        const cached = this._layer;
        if (cached !== null && cached.signature === signature) {
          return cached.layer;
        }
        // No workflows watch this dataset yet, but the column still renders — its
        // header hosts "Add automation", so a dataset with no automations is
        // where you attach the first one. `buildAutomationProjection` degrades to
        // a constant column (no join onto the runs table, which is never loaded
        // in this case), and the cells stay empty.
        const layer: DatasetPreviewLayer = {
          id: 'dataset-automations',
          project: ({ baseAlias, alias }) =>
            buildAutomationProjection({
              datasetId,
              rowIdColumn,
              workflows,
              baseAlias,
              runsAlias: alias('runs'),
              loadSummaryStats: async ({ sourceSql }) =>
                loadAutomationStateBreakdown(await this._duckDb, {
                  sourceSql,
                  workflows,
                }),
            }),
          present: () => ({
            // The pinned summary column first, then one output column per
            // workflow the user chose to expand. The summary stays a single
            // rolled-up column — per-workflow *state* lives in its tick strip
            // and the expanded panel — while the expanded columns surface a
            // different thing: each run's final output as a plain cell value.
            columns: [
              summaryColumn({
                workflows,
                rowIdColumn,
                onRun: this.runRow,
                readyWatchable: this.readyWatchable,
                settledWatchable: this.settledWatchable,
                ownerOrgNameWatchable: this.ownerOrgNameWatchable,
              }),
              ...expandedColumns,
            ],
            subscribe: this.registerGrid,
            // No expandable per-workflow panel when nothing is bound — there is
            // no run detail to show, only the header's "Add automation".
            ...(workflows.length > 0 && {
              rowDetail: {
                height: automationRowDetailHeight(workflows.length),
                render: (context) => (
                  <RowDetail
                    row={context.row}
                    workflows={workflows}
                    rowIdColumn={rowIdColumn}
                    onRun={this.runRow}
                    ownerOrgNameWatchable={this.ownerOrgNameWatchable}
                  />
                ),
              },
            }),
          }),
        };
        this._layer = { signature, layer };
        return layer;
      }
    );
  }

  /**
   * Points the model at a dataset. Idempotent, so the page can call it on
   * every render: an unchanged source neither republishes nor refetches.
   */
  setSource(source: DatasetAutomationsSource): void {
    if (sameSource(this._source.snapshot(), source)) {
      return;
    }
    const previousDatasetId = this._source.snapshot().datasetId;
    this._source.set(source);
    void this._snapshot.refresh();
    // A different dataset is a different set of batches, so anything tracked for
    // the old one is dropped rather than carried across.
    if (source.datasetId !== previousDatasetId) {
      this._runProgress.set(null);
      if (source.datasetId !== null) {
        void this.adoptActiveRunGroup(source.datasetId);
      }
    }
  }

  /**
   * Points the model at the dataset's saved "expanded workflows" setting.
   * Idempotent — the content-equality watchable ignores an unchanged list — so
   * the page can call it on every render. No refetch: the output values already
   * ride on the overlay this model loads; this only decides which of them become
   * columns.
   */
  setExpandedWorkflowIds(workflowIds: readonly string[]): void {
    this._expandedWorkflowIds.set(workflowIds);
  }

  /**
   * The seam the grid registers itself through, so the model can push new cell
   * values into rows already on screen. An arrow property: the overlay hands
   * this to the grid, and a fresh function would re-register on every rebuild.
   */
  readonly registerGrid = (grid: DuckDbLayerGrid): (() => void) => {
    this._grid = grid;
    return () => {
      this._grid = null;
    };
  };

  /**
   * Pushes one row's automation cells back to whatever DuckDB now holds.
   *
   * Shared by the live update and the optimistic marks below, because both
   * change one row's records and neither may re-run the page query — that is
   * what reorders rows under the cursor.
   */
  private readonly repaintRow = async (
    db: DuckDbWriteTarget,
    datasetId: string,
    rowId: string
  ): Promise<void> => {
    const values = await selectOverlayValuesForRows(db, {
      datasetId,
      datasetRowIds: [rowId],
      workflows: this._wiring.snapshot(),
    });
    const row = values.get(rowId);
    if (row !== undefined) {
      this._grid?.patch(rowId, row);
    }
  };

  /**
   * The workflows a click on one of the row panel's Run buttons actually
   * starts, which is what the optimistic mark has to cover.
   *
   * Read off the wiring rather than taken from the caller because a trigger
   * fans out: firing one with no workflow named runs everything bound to it,
   * and a mark on fewer than that would leave the rest of the row looking
   * untouched until the round trip came back.
   */
  private readonly workflowIdsFor = (
    triggerId?: string,
    workflowId?: string
  ): string[] =>
    this._wiring
      .snapshot()
      .filter(
        (workflow) =>
          (triggerId === undefined || workflow.triggerId === triggerId) &&
          (workflowId === undefined || workflow.id === workflowId)
      )
      .map((workflow) => workflow.id);

  /** Paints these workflows as pending on the row, and repaints it. */
  private readonly markRowPending = (
    rowId: string,
    workflowIds: readonly string[]
  ): Promise<void> =>
    this.writeRowMarks(rowId, workflowIds, markAutomationRunsPending);

  /** Takes that back, for a request that turned out to start nothing. */
  private readonly clearRowPending = (
    rowId: string,
    workflowIds: readonly string[]
  ): Promise<void> =>
    this.writeRowMarks(rowId, workflowIds, clearAutomationRunsPending);

  /**
   * Failures are swallowed to a console line on purpose: this is a local
   * nicety in front of a request that is running regardless, and a DuckDB
   * write that throws must not turn into a rejected `runRow` — the panel
   * fires that without a rejection handler.
   */
  private readonly writeRowMarks = async (
    rowId: string,
    workflowIds: readonly string[],
    write: (
      db: DuckDbWriteTarget,
      params: {
        datasetId: string;
        datasetRowId: string;
        workflowIds: readonly string[];
      }
    ) => Promise<void>
  ): Promise<void> => {
    const { datasetId } = this._source.snapshot();
    if (datasetId === null || workflowIds.length === 0) {
      return;
    }
    try {
      const db = await this._duckDb;
      await write(db, { datasetId, datasetRowId: rowId, workflowIds });
      await this.repaintRow(db, datasetId, rowId);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[dataset-automations] pending mark failed', error);
    }
  };

  /**
   * Paints every row currently on screen as pending for these workflows, so a
   * bulk run acknowledges the click immediately instead of leaving the grid
   * unchanged until the first run's broadcast lands.
   *
   * Bounded to the loaded rows — a page, not the dataset — which is all the user
   * can see anyway; rows scrolled to later paint from the live events that will
   * have arrived by then. Optimistic and self-correcting, exactly like the
   * single-row mark: each cell is superseded the moment its run broadcasts, and
   * the next overlay reconcile replaces the table wholesale, dropping any mark
   * whose row the dispatch turned out not to run (a replay re-evaluates, so it
   * need not run every row it is pointed at).
   *
   * Failures are swallowed to a console line: this sits in front of a request
   * that is running regardless, so a DuckDB write that throws must not reject the
   * run it is decorating.
   */
  private readonly markLoadedRowsPending = async (
    workflowIds: readonly string[]
  ): Promise<void> => {
    const { datasetId } = this._source.snapshot();
    const grid = this._grid;
    if (datasetId === null || grid === null || workflowIds.length === 0) {
      return;
    }
    const datasetRowIds = grid.loadedRowIds().map((rowId) => String(rowId));
    if (datasetRowIds.length === 0) {
      return;
    }
    try {
      const db = await this._duckDb;
      await markAutomationRunsPendingForRows(db, {
        datasetId,
        datasetRowIds,
        workflowIds,
      });
      await repaintLoadedRows(db, {
        datasetId,
        workflows: this._wiring.snapshot(),
        grid,
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[dataset-automations] bulk pending mark failed', error);
    }
  };

  /**
   * Runs one row: a single workflow, a whole trigger, or everything watching
   * this dataset.
   *
   * `workflowId` narrows the trigger's fan-out to that one workflow — without
   * it, firing a trigger runs every workflow bound to it, which is not what a
   * button sitting next to one workflow's name promises.
   *
   * Returns a promise rather than tracking which row is in flight here: that
   * state would have to enter the overlay, and the overlay's identity rebuilds
   * the entire view model — new columns, new sorts, new selection. The panel
   * awaits this and owns its own busy flag instead.
   *
   * The cells, separately, are painted as pending before the request is waited
   * on. A queued run is only visible here once the server has answered *and*
   * broadcast it, and until then every part of the row except the button's own
   * spinner still described the last run — a click that looked like it had done
   * nothing, on the control most likely to be clicked twice.
   */
  readonly runRow = async (
    rowId: string,
    triggerId?: string,
    workflowId?: string
  ): Promise<void> => {
    // One trigger, or every trigger watching this dataset.
    const targets =
      triggerId === undefined
        ? [
            ...new Set(
              this._wiring.snapshot().map((workflow) => workflow.triggerId)
            ),
          ]
        : [triggerId];
    // Started before the mark is written, not after: the mark is a local
    // DuckDB write and a repaint, and the request is what the user is actually
    // waiting on. Awaited afterwards, so a withdrawal below cannot race ahead
    // of the write it is withdrawing.
    const request = Promise.all(
      targets.map((target) =>
        this._client.resource.workflow.triggerNow({
          triggerId: target,
          rowIds: [rowId],
          ...(workflowId !== undefined && { workflowIds: [workflowId] }),
        })
      )
    );
    const pendingWorkflowIds = this.workflowIdsFor(triggerId, workflowId);
    await this.markRowPending(rowId, pendingWorkflowIds);

    const results = await request;
    const failed = results.filter((result) => result.isErr()).length;
    if (failed > 0) {
      toast.error(`Could not start ${failed} of ${results.length} automations`);
      await this.clearRowPending(rowId, pendingWorkflowIds);
      return;
    }
    // A request that succeeds while dispatching nothing is the dead-button
    // case: the binding was removed between the grid reading it and the click.
    const dispatched = results.reduce(
      (total, result) =>
        total + (result.isOk() ? result.value.dispatchedWorkflowRuns : 0),
      0
    );
    if (dispatched === 0) {
      toast.error('Nothing to run — that automation is no longer bound');
      await this.clearRowPending(rowId, pendingWorkflowIds);
      return;
    }
    // Marks for runs that did start are left in place rather than cleared
    // here. The server has them now, so the next thing to describe the pair —
    // its run broadcast, or the reconcile's overlay — replaces the mark with
    // the truth; clearing it now would only put the cell back to its old state
    // for the rest of that wait.
  };

  /**
   * Runs a set of rows against a set of workflows, reporting progress as it goes.
   *
   * Batched rather than sent as one request: the server dispatches sequentially
   * per (row x workflow), so a single call for a large selection is one very long
   * request that can neither report progress nor be stopped. Batches also bound
   * what a failure costs — everything already dispatched keeps running.
   *
   * Takes a cursor rather than an array of ids, and pulls one batch at a time
   * from it. The loop below can run for a long stretch of wall clock, and the
   * whole id set sitting in the tab for all of it was the cost this avoids — the
   * ids stay in DuckDB, where the dataset already is. The cursor belongs to the
   * caller, which is what closes it.
   *
   * Progress is only what the server confirms it queued, batch by batch. The
   * outcomes of those runs are not tallied: that would need a record of the
   * batch server-side, and the per-row state the grid already shows is where
   * they land instead.
   */
  readonly runRows = async (input: {
    rows: DatasetRowIdCursor;
    workflowIds: readonly string[];
  }): Promise<void> => {
    const { datasetId } = this._source.snapshot();
    const workflows = this._wiring
      .snapshot()
      .filter((workflow) => input.workflowIds.includes(workflow.id));
    if (
      datasetId === null ||
      input.rows.total === 0 ||
      workflows.length === 0
    ) {
      return;
    }

    // Grouped so each request names one trigger and the workflows on it: a
    // trigger fans out to its own bindings, so asking for a workflow through the
    // wrong trigger dispatches nothing at all.
    const byTrigger = new Map<string, string[]>();
    for (const workflow of workflows) {
      byTrigger.set(workflow.triggerId, [
        ...(byTrigger.get(workflow.triggerId) ?? []),
        workflow.id,
      ]);
    }

    // Minted here so every page below names the same batch without a round
    // trip to learn its id, and so Stop has a handle on it from the first page.
    const groupId = v4();

    this._stopRequested = false;
    this._runProgress.set({
      ...NO_COUNTS,
      groupId,
      kind: 'dispatch',
      dispatched: 0,
      total: input.rows.total * workflows.length,
      dispatching: true,
    });

    // Acknowledge the click on every row in view before anything is sent.
    // These scopes force-fire, so a loaded row is one that will run.
    void this.markLoadedRowsPending(workflows.map((workflow) => workflow.id));

    // Pages are *recorded* here, not fired. Nothing runs until the handover
    // below, so a tab closed part way through leaves rows collected and no runs
    // at all — where the old loop dispatched as it went and stranded whatever
    // it had not reached.
    let collected = 0;
    for await (const batch of input.rows.batches(RUN_BATCH_SIZE)) {
      if (this._stopRequested) {
        // Nothing has run, so there is nothing to cancel: the collected rows
        // are inert and the sweep tidies them away.
        this._runProgress.set(null);
        return;
      }
      for (const [triggerId, workflowIds] of byTrigger) {
        const result =
          await this._client.resource.workflow.collectDatasetRunGroupRows({
            datasetId,
            groupId,
            triggerId,
            rowKeys: [...batch],
            workflowIds,
          });
        if (result.isErr()) {
          const message =
            result.error.t === 'application'
              ? result.error.err.message ?? 'Could not start these runs'
              : 'Could not start these runs';
          toast.error(message);
          this._runProgress.set(null);
          return;
        }
        collected = result.value.collected;
      }
    }

    // Nothing collected is the dead-button case: the bindings went away between
    // the grid reading them and the click.
    if (collected === 0) {
      toast.error('Nothing to run — those automations are no longer bound');
      this._runProgress.set(null);
      return;
    }

    // Hand it over. From here the server drains the batch and closes it when
    // the last row has fired, so this tab can go away without stranding it.
    for (const [triggerId, workflowIds] of byTrigger) {
      const started = await this._client.resource.workflow.startDatasetRunGroup(
        { datasetId, groupId, triggerId, workflowIds }
      );
      if (started.isErr()) {
        toast.error('Collected the rows, but could not start the run');
        this._runProgress.set(null);
        return;
      }
    }

    this.settleDispatch();
  };

  /**
   * Re-runs every row of the dataset against a set of workflows, server-side.
   *
   * Deliberately not {@link runRows} over a cursor of every id. That path is the
   * browser naming rows one batch at a time, which for a whole dataset is a very
   * long request loop whose progress lives in a tab — close it and the run stops
   * half-done. This hands the whole thing over instead: the server forgets what
   * each (trigger, workflow) pair has observed, and the evaluator replays the
   * dataset through its ordinary path, with the crash-safety, the per-dataset
   * lock and the dispatch cap that path already has.
   *
   * The trade is real and deliberate: this *re-evaluates* rather than force-fires.
   * A row is dispatched if it meets the trigger's criterion, where {@link runRows}
   * fires every row it is handed regardless. For "run all" that is the more
   * honest reading of the button, and for a `new_row` trigger the two agree
   * anyway — forgetting the observed state makes every key new again.
   *
   * Nothing here reports how far it gets. The evaluator dispatches it across
   * many evaluations, and this tab is not part of that — what it keeps is the
   * group id, which is enough to stop the replay again.
   */
  readonly replayAll = async (input: {
    workflowIds: readonly string[];
    /** Skip rows whose latest run is current — judged server-side, per row. */
    onlyNeedsRun: boolean;
  }): Promise<void> => {
    const { datasetId } = this._source.snapshot();
    const workflows = this._wiring
      .snapshot()
      .filter((workflow) => input.workflowIds.includes(workflow.id));
    if (datasetId === null || workflows.length === 0) {
      return;
    }

    const groupId = v4();
    // Handing the dataset to the server can take a moment, and until it returns
    // the header shows nothing — the run progress is only set on success below.
    // A loading toast is the one thing between the click and that, so the click
    // does not read as having done nothing.
    const startingToast = toast.loading('Starting run…');
    const started = await this._client.resource.workflow.replayDataset({
      datasetId,
      groupId,
      workflowIds: workflows.map((workflow) => workflow.id),
      onlyNeedsRun: input.onlyNeedsRun,
    });
    if (started.isErr()) {
      const message =
        started.error.t === 'application'
          ? started.error.err.message ?? 'Could not start this run'
          : 'Could not start this run';
      startingToast.error(message);
      return;
    }
    if (started.value.replayedPairs === 0) {
      startingToast.error(
        'Nothing to run — those automations are no longer bound'
      );
      return;
    }
    startingToast.success('Run started — rows update as they finish');

    this._stopRequested = false;
    // `kind: 'replay'` and `dispatching: false`: the evaluator sends the
    // batches, not this tab, so there is no loop here for Stop to interrupt and
    // no count to climb. Held so Stop has a group to cancel — and so the header
    // can show the replay is running in the background rather than looking as if
    // the click did nothing.
    this._runProgress.set({
      ...NO_COUNTS,
      groupId,
      kind: 'replay',
      dispatched: 0,
      total: 0,
      dispatching: false,
    });

    // Acknowledge the click on the rows in view. A replay re-evaluates rather
    // than force-fires, so some of these may not actually run; the mark is
    // optimistic and self-correcting — the live run events supersede each cell,
    // and the next overlay reconcile drops any mark whose row did not run.
    // Skipped for a needs-run replay, where most rows in view may already be
    // done and would flash pending for nothing.
    if (!input.onlyNeedsRun) {
      void this.markLoadedRowsPending(workflows.map((workflow) => workflow.id));
    }
  };

  /**
   * Coalesces the batch refreshes a fan-out would otherwise trigger.
   *
   * Every run finishing broadcasts, and a batch can hold thousands, so refreshing
   * per event would be one request per run to redraw a number that only has a
   * hundred distinct values. Trailing edge, so a burst costs one read at its end
   * and the bar still lands on the true figure.
   */
  private _runProgressRefresh: ReturnType<typeof setTimeout> | null = null;

  private scheduleRunProgressRefresh(): void {
    if (this._runProgressRefresh !== null) {
      return;
    }
    this._runProgressRefresh = setTimeout(() => {
      this._runProgressRefresh = null;
      void this.refreshRunProgress();
    }, RUN_PROGRESS_REFRESH_MS);
  }

  /**
   * Re-reads the batch's counts from the server.
   *
   * The counts are not accumulated here from the events this tab saw, because a
   * tab that was not open for the first half of a batch never saw them. The
   * server counts the batch's runs whenever asked, so a tab that joins late is
   * as right as one that watched the whole thing.
   *
   * Clears the batch once it has settled *and* this tab is no longer sending.
   * Both halves matter: a settled reading during the dispatch loop only means
   * the runs sent so far have finished, and dropping the batch then would take
   * the Stop button away mid-run.
   */
  private readonly refreshRunProgress = async (): Promise<void> => {
    const current = this._runProgress.snapshot();
    const { datasetId } = this._source.snapshot();
    if (current === null || datasetId === null) {
      return;
    }
    const result = await this._client.resource.workflow.datasetRunGroups({
      datasetId,
      limit: 20,
    });
    if (result.isErr()) {
      return;
    }
    const group = result.value.groups.find(
      (candidate) => candidate.groupId === current.groupId
    );
    // Still the batch this tab is tracking? A different one having started
    // since must not overwrite it.
    const latest = this._runProgress.snapshot();
    if (
      group === undefined ||
      latest === null ||
      latest.groupId !== current.groupId
    ) {
      return;
    }
    if (group.settled && !latest.dispatching) {
      this._runProgress.set(null);
      return;
    }
    this._runProgress.set({
      ...latest,
      runs: group.total,
      finished: group.terminal,
      percentComplete: group.percentComplete,
      estimated: group.estimated,
      settled: group.settled,
    });
  };

  /**
   * Picks up a batch already running on this dataset.
   *
   * The reason the record exists. A bulk run used to live in the memory of the
   * tab that started it, so reloading lost the progress *and* the Stop button
   * while the runs carried on regardless — and a colleague watching the same
   * dataset saw nothing at all. Called when the model learns which dataset it is
   * showing, which is the first moment it could ask.
   *
   * Only ever adopts when this tab is tracking nothing. A tab in the middle of
   * its own dispatch loop knows more about that batch than this read does.
   */
  private readonly adoptActiveRunGroup = async (
    datasetId: string
  ): Promise<void> => {
    if (this._runProgress.snapshot() !== null) {
      return;
    }
    const result = await this._client.resource.workflow.datasetRunGroups({
      datasetId,
      activeOnly: true,
      limit: 1,
    });
    if (result.isErr()) {
      return;
    }
    const [group] = result.value.groups;
    if (group === undefined || group.settled) {
      return;
    }
    // Lost the race with a run this tab started in the meantime.
    if (this._runProgress.snapshot() !== null) {
      return;
    }
    this._runProgress.set({
      groupId: group.groupId,
      kind: group.origin === 'replay' ? 'replay' : 'dispatch',
      dispatched: group.total,
      total: group.total,
      // Whoever is still sending it, it is not this tab.
      dispatching: false,
      runs: group.total,
      finished: group.terminal,
      percentComplete: group.percentComplete,
      estimated: group.estimated,
      settled: group.settled,
      adopted: true,
    });
  };

  /**
   * Marks the handover: this tab has finished naming rows.
   *
   * It no longer closes anything. Closing follows from the server's drain
   * running dry, which is the point of handing over — a tab that does not know
   * whether its rows have fired cannot honestly say the batch is complete.
   * What it keeps is the watch and the Stop button.
   */
  private settleDispatch(): void {
    const current = this._runProgress.snapshot();
    if (current === null) {
      return;
    }
    this._runProgress.set({ ...current, dispatching: false });
    void this.refreshRunProgress();
  }

  /**
   * Stops a bulk run: no further batches are dispatched, and everything already
   * queued is cancelled.
   *
   * Both halves matter. The local flag is what stops this tab sending the next
   * batch — the server cannot know about batches that do not exist yet — and the
   * request is what stops the work already handed over, which for a replay is
   * the only half there is.
   *
   * Cancelling server-side rather than looping run ids here is what makes this
   * finishable: a batch can hold thousands of runs, and the group id names them
   * all in one request.
   */
  readonly stopRun = async (): Promise<void> => {
    this._stopRequested = true;
    const current = this._runProgress.snapshot();
    const { datasetId } = this._source.snapshot();
    if (current === null || datasetId === null) {
      return;
    }
    // Cleared before the round trip returns: a Stop button that stays live until
    // then reads as a button that did nothing.
    this._runProgress.set(null);

    const result = await this._client.resource.workflow.cancelDatasetRunGroup({
      datasetId,
      groupId: current.groupId,
    });
    if (result.isErr()) {
      toast.error('Stopped dispatching, but could not cancel the queued runs');
    }
  };

  dispose(): void {
    this._snapshot.unsubscribe();
    this._live.unsubscribe();
    if (this._runProgressRefresh !== null) {
      clearTimeout(this._runProgressRefresh);
      this._runProgressRefresh = null;
    }
  }
}

/**
 * Reads the page's {@link DatasetAutomationsModel} out of scope and keeps it
 * pointed at what is currently on screen.
 *
 * The model is registered by the page's `ModelScopeFactory`, not built here.
 * That factory creates its models *inside* an effect and ref-counts disposal,
 * so React's StrictMode mount/unmount/remount cycle cannot leave a live page
 * holding a model whose watchables have already unsubscribed from the event
 * bus — which is exactly what a `useMemo` plus a disposing cleanup does.
 */
export const useDatasetAutomations = ({
  modelId,
  datasetId,
  artifactId,
  rowIdColumn,
  expandedWorkflowIds,
}: {
  /** The scope id the model was registered under: the page's dataset id. */
  modelId: string;
  datasetId: string | null;
  /** The artifact this browser loaded; bounds the overlay to its rows. */
  artifactId: string | null;
  rowIdColumn: string | null;
  /**
   * The dataset's saved "expand these workflows into columns" setting. A stable
   * reference from the caller: passing a fresh array of identical ids is
   * harmless (the model compares by content) but wastes a render.
   */
  expandedWorkflowIds: readonly string[];
}): DatasetAutomationsModel => {
  const model = useInScopeModel(DatasetAutomationsModel, modelId);

  React.useEffect(() => {
    model.setSource({ datasetId, artifactId, rowIdColumn });
  }, [artifactId, datasetId, model, rowIdColumn]);

  React.useEffect(() => {
    model.setExpandedWorkflowIds(expandedWorkflowIds);
  }, [model, expandedWorkflowIds]);

  return model;
};
